import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowLeft, FaRegFileAlt, FaSearch } from "react-icons/fa";
import { AiOutlineCloudUpload, AiOutlineDelete } from "react-icons/ai";
import {
  MdRefresh,
  MdAddCircleOutline,
  MdOutlineRemoveRedEye
} from "react-icons/md";
import { BiErrorCircle } from "react-icons/bi";
import "./Upload.scss";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import Table from "common/components/custom/sorting-table/SortingTable";
import CollapsibleDocumentList from "../../components/CollapsibleDocumentList";
import SimplePagination from "../../components/SimplePagination";
import AzureBlobModal from "../../components/AzureBlobModal";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderIcon from "@mui/icons-material/Folder";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import DesignServicesOutlinedIcon from "@mui/icons-material/DesignServicesOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  blobStorageService,
  BlobStorageService
} from "common/services/blob-storage.service";

import {
  useGetDocumentsQuery,
  useGetUploadedDocumentsQuery,
  useLazyGetDocumentsQuery,
  useGetFlowConfigurationByIdQuery
} from "../../data/api/queries/processing-hub-queries";
import {
  useUploadDocumentsMutation,
  useMultiUploadDocumentsMutation,
  useDeleteDocumentMutation,
  useRefreshDocumentsMutation
} from "../../data/api/commands/processing-hub-commands";
import { ProcessingHubService } from "../../data/services/processing-hub.service";

import { useLazyDownloadDocumentQuery } from "modules/documents/data/api/queries/downloadDocument";
import { DocumentStatus } from "common/enums/document-status.enum";

import {
  SearchDocumentsRequest,
  DocumentTypeInformation
} from "../../data/models/processing-hub.model";
import { formattedDate } from "common/utils/date";

// Configuration constants
const UPLOAD_BATCH_SIZE = 20; // Maximum files per batch

const Upload: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const flowConfigId = id;

  // Get flow config from navigation state (passed from ProcessingHub)
  const flowConfigFromState = location.state?.flowConfig as
    | DocumentTypeInformation
    | undefined;

  // Fetch flow configuration by ID if flowConfigId is present and no flowConfig in state
  const {
    data: flowConfigData,
    isLoading: isLoadingFlowConfig,
    error: flowConfigError
  } = useGetFlowConfigurationByIdQuery(flowConfigId!, {
    skip: !flowConfigId || !!flowConfigFromState
  });

  // Use flowConfig from state if available, otherwise use fetched flowConfig
  const flowConfig = flowConfigFromState || flowConfigData;

  // Helper functions to safely access properties from different config types
  const getFlowId = (config: any) => {
    return config?.flow_id ?? config?.id;
  };

  const getDocumentTypeName = (config: any) => {
    return config?.document_type || config?.name || config?.flow_name;
  };

  // Determine document type based on the document_type field from the flow config
  const documentType: "engineering" | "contract" | "invoice" | "others" =
    getDocumentTypeName(flowConfig)?.toLowerCase() === "engineering"
      ? "engineering"
      : getDocumentTypeName(flowConfig)?.toLowerCase() === "invoice"
      ? "invoice"
      : getDocumentTypeName(flowConfig)?.toLowerCase() === "others"
      ? "others"
      : "contract";

  const [showTooltipId, setShowTooltipId] = useState<number | null>(null);
  const [selectedPurpose, setSelectedPurpose] = useState<
    "drawing" | "documents"
  >("drawing");
  const [files, setFiles] = useState<File[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isAzureBlobModalOpen, setIsAzureBlobModalOpen] = useState(false);

  // File input reset mechanism
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State for folder vs file selection mode
  const [isFolderMode, setIsFolderMode] = useState<boolean>(false);

  // State for list view mode (table vs collapsible)
  const [isCollapsibleView, setIsCollapsibleView] = useState<boolean>(false);

  // Refresh spinning state
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Batch upload progress states
  const [batchUploadProgress, setBatchUploadProgress] = useState<{
    isUploading: boolean;
    currentBatch: number;
    totalBatches: number;
    totalFiles: number;
  }>({
    isUploading: false,
    currentBatch: 0,
    totalBatches: 0,
    totalFiles: 0
  });

  const [searchParams, setSearchParams] = useState<SearchDocumentsRequest>({
    searchTerm: "",
    page: 1,
    pageSize: 10
  });

  const {
    data: documentsData,
    isLoading: isLoadingDocuments,
    error: documentsError,
    refetch: refetchDocuments
  } = useGetUploadedDocumentsQuery({
    hasDuplicate: false,
    page: currentPage,
    size: pageSize,
    search: searchTerm,
    sort: "",
    dir: "",
    showDuplicates: false,
    useEngApi: true,
    flowId: getFlowId(flowConfig)
  });

  const [uploadDocuments, { isLoading: isUploading }] =
    useUploadDocumentsMutation();
  const [multiUploadDocuments, { isLoading: isMultiUploading }] =
    useMultiUploadDocumentsMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [refreshDocuments] = useRefreshDocumentsMutation();

  // Download functionality
  const [
    downloadFile,
    { isError, isFetching, isLoading: isDownloading, isSuccess }
  ] = useLazyDownloadDocumentQuery();

  // Generate consistent color based on name
  const generateColorFromName = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      const char = name.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    // Generate a bright, readable color
    const hue = Math.abs(hash) % 360;
    const saturation = 65 + (Math.abs(hash) % 20); // 65-85%
    const lightness = 45 + (Math.abs(hash) % 20); // 45-65%

    // Convert HSL to hex
    const hslToHex = (h: number, s: number, l: number): string => {
      l /= 100;
      const a = (s * Math.min(l, 1 - l)) / 100;
      const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color)
          .toString(16)
          .padStart(2, "0");
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    return hslToHex(hue, saturation, lightness);
  };

  // Transform API response to match DocumentTable interface
  const transformDocumentsData = (apiData: any) => {
    // Handle different API response structures
    let dataArray = [];

    if (Array.isArray(apiData)) {
      dataArray = apiData;
    } else if (apiData && Array.isArray(apiData.data)) {
      dataArray = apiData.data;
    } else if (apiData && Array.isArray(apiData.items)) {
      dataArray = apiData.items;
    } else if (apiData && Array.isArray(apiData.documents)) {
      dataArray = apiData.documents;
    } else {
      console.warn("API response is not in expected format:", apiData);
      return [];
    }

    return dataArray.map((doc: any, index: number) => ({
      id: doc.documentGuid || doc.document_id || doc.id || index + 1,
      documentGuid: doc.documentGuid || doc.document_id || doc.id,
      fileName: doc.name || "Unknown File",
      flowName: doc.gpName,
      flowId: doc.flowId || doc.flow_id,
      docType: doc.heading,
      documentType:
        doc.FileCategory ||
        doc.fileCategory ||
        doc.file_category ||
        doc.category ||
        "",
      uploadedDate: doc.createdDate
        ? formattedDate(doc.createdDate)
        : formattedDate(new Date().toISOString()),
      uploadedBy: doc.createdBy
        ? {
            initial: doc.createdBy.charAt(0).toUpperCase(),
            name: doc.createdBy,
            color: generateColorFromName(doc.createdBy) // Consistent color based on name
          }
        : undefined,
      activityStatus: `Version ${doc.versionNumber}`,
      status: doc.status || "Uploaded"
    }));
  };

  const getDocumentGuidById = (documentId: string | number) => {
    const transformed = transformDocumentsData(documentsData);
    const match = transformed.find(
      (doc: any) => doc.id === documentId || doc.documentGuid === documentId
    );
    return match?.documentGuid || documentId;
  };

  const handleViewDocument = (documentId: string | number) => {
    const documentGuid = getDocumentGuidById(documentId);
    navigate(`/documents/${documentGuid}?page=1&size=10`, {
      state: {
        path: "/processing-hub",
        useEngApi: documentType === "engineering"
      }
    });
  };

  const handleEditDocument = (documentId: string | number) => {
    const documentGuid = getDocumentGuidById(documentId);
    navigate(`/relationship-validation/${documentGuid}`, {
      state: {
        path: "/processing-hub",
        useEngApi: documentType === "engineering",
        editMode: true
      }
    });
  };

  // Filter documents by current flow ID
  const filterDocumentsByFlowId = (documents: any[]) => {
    const currentFlowId = getFlowId(flowConfig);
    if (!currentFlowId) {
      return documents; // If no flowId, show all documents
    }
    return documents.filter((doc) => {
      // Convert both to strings for comparison to handle different types
      const docFlowId = doc.flowId?.toString();
      const currentFlowIdStr = currentFlowId.toString();
      return docFlowId === currentFlowIdStr;
    });
  };

  // Reset file input to allow selecting the same files/folder again
  const resetFileInput = () => {
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;
    if (fileList) {
      const allFiles = Array.from(fileList);

      // Filter out directories and only keep files
      const validFiles = allFiles.filter((file) => {
        // Check if it's actually a file (has size or type)
        return file.size > 0 || file.type !== "";
      });

      if (validFiles.length > 0) {
        setFiles(validFiles);
      } else {
        console.warn("No valid files found in the selected folder");
        // alert(
        //   "No valid files found in the selected folder. Please select a folder containing files."
        // );
      }
    }
  };

  const handleUploadClick = (event?: React.MouseEvent) => {
    // Disable file selection if files are already selected
    if (files.length > 0) {
      return;
    }

    const uploadMethod = flowConfig?.upload_method || "manual"; // Default to manual

    // If upload method is blob, prevent the label from triggering file input
    if (uploadMethod === "blob" && event) {
      event.preventDefault();
      event.stopPropagation();
    }

    switch (uploadMethod) {
      case "blob":
        setIsAzureBlobModalOpen(true);
        break;
      case "sharepoint":
        // TODO: Show SharePoint picker
        break;
      case "manual":
      default: {
        // For manual upload, let the label's htmlFor handle the file input trigger
        break;
      }
    }
  };

  const handleFileUploadClick = () => {
    setIsFolderMode(false);
    resetFileInput();
    // Trigger file input click directly using React ref
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 50);
  };

  const handleFolderUploadClick = () => {
    setIsFolderMode(true);
    resetFileInput();
    // Trigger file input click directly using React ref
    setTimeout(() => {
      fileInputRef.current?.click();
    }, 50);
  };

  const handleAzureBlobUpload = async (blobFiles: File[]) => {
    if (blobFiles.length === 0) {
      return;
    }

    try {
      // Add all files to the upload queue immediately
      setFiles((prevFiles) => [...prevFiles, ...blobFiles]);
    } catch (error) {
      console.error("Error handling Azure Blob files:", error);
    }
  };

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const term = event.target.value;
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
    setSearchParams((prev) => ({ ...prev, searchTerm: term }));
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await refetchDocuments();
    } catch (error) {
      console.error("Error refreshing documents:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    // Map the selected purpose to the API's file_category value
    const fileCategory =
      selectedPurpose === "drawing"
        ? "Engineering Drawing"
        : "Engineering Document";
    const uploadDocumentType = getDocumentTypeName(flowConfig) || "engineering";

    try {
      const totalBatches = Math.ceil(files.length / UPLOAD_BATCH_SIZE);

      // Initialize batch upload progress
      setBatchUploadProgress({
        isUploading: true,
        currentBatch: 0,
        totalBatches: totalBatches,
        totalFiles: files.length
      });

      // Add a small delay to ensure UI updates
      await new Promise((resolve) => setTimeout(resolve, 100));

      // If files are within batch size limit, upload in a single batch
      if (files.length <= UPLOAD_BATCH_SIZE) {
        setBatchUploadProgress((prev) => ({ ...prev, currentBatch: 1 }));

        // Add another small delay to show progress update
        await new Promise((resolve) => setTimeout(resolve, 200));

        const formData = new FormData();

        for (const file of files) {
          if ((file as any).isAzureBlob) {
            const blobUrl = (file as any).blobUrl;
            try {
              const { blob, contentType: fetchedContentType } =
                await blobStorageService.fetchBlobFromUrl(blobUrl);

              let contentType = fetchedContentType;
              if (!contentType || contentType === "application/octet-stream") {
                contentType = BlobStorageService.getContentTypeFromFilename(
                  file.name
                );
              }

              const typedBlob = new Blob([blob], { type: contentType });
              formData.append("files", typedBlob, file.name);
            } catch (fetchError) {
              console.error(
                `Error fetching Azure blob ${file.name}:`,
                fetchError
              );
              continue;
            }
          } else {
            const blob = new Blob([file], { type: file.type });
            formData.append("files", blob, file.name);
          }
        }

        const result = await multiUploadDocuments({
          files: formData,
          gpName: "BULKUPLOAD",
          isReferenceDocument: true,
          referenceType: "GP",
          flowId: getFlowId(flowConfig),
          documentType: uploadDocumentType,
          fileCategory
        }).unwrap();

        if (result && Array.isArray(result)) {
          const failedUploads = result.filter((item: any) => !item.isUploaded);
          if (failedUploads.length > 0) {
            console.error("Some files failed to upload:", failedUploads);
          }
        }
      } else {
        // Process files in batches
        let successfulBatches = 0;
        let failedBatches = 0;

        for (let i = 0; i < totalBatches; i++) {
          const startIndex = i * UPLOAD_BATCH_SIZE;
          const endIndex = Math.min(
            startIndex + UPLOAD_BATCH_SIZE,
            files.length
          );
          const batchFiles = files.slice(startIndex, endIndex);

          // Update progress before starting batch
          setBatchUploadProgress((prev) => ({ ...prev, currentBatch: i + 1 }));

          // Add delay to ensure UI updates
          await new Promise((resolve) => setTimeout(resolve, 300));

          try {
            const formData = new FormData();

            for (const file of batchFiles) {
              if ((file as any).isAzureBlob) {
                const blobUrl = (file as any).blobUrl;
                try {
                  const { blob, contentType: fetchedContentType } =
                    await blobStorageService.fetchBlobFromUrl(blobUrl);

                  let contentType = fetchedContentType;
                  if (
                    !contentType ||
                    contentType === "application/octet-stream"
                  ) {
                    contentType = BlobStorageService.getContentTypeFromFilename(
                      file.name
                    );
                  }

                  const typedBlob = new Blob([blob], { type: contentType });
                  formData.append("files", typedBlob, file.name);
                } catch (fetchError) {
                  console.error(
                    `Error fetching Azure blob ${file.name}:`,
                    fetchError
                  );
                  continue;
                }
              } else {
                const blob = new Blob([file], { type: file.type });
                formData.append("files", blob, file.name);
              }
            }

            // Upload current batch
            const result = await multiUploadDocuments({
              files: formData,
              gpName: "BULKUPLOAD",
              isReferenceDocument: true,
              referenceType: "GP",
              flowId: getFlowId(flowConfig),
              documentType: uploadDocumentType,
              fileCategory
            }).unwrap();

            if (result && Array.isArray(result)) {
              const failedUploads = result.filter(
                (item: any) => !item.isUploaded
              );
              if (failedUploads.length > 0) {
                console.error(
                  `Some files in batch ${i + 1} failed to upload:`,
                  failedUploads
                );
              }
            }

            successfulBatches++;
          } catch (batchError) {
            failedBatches++;
            console.error(`❌ Error uploading batch ${i + 1}:`, batchError);

            // For now, continue with other batches even if one fails
            // You can modify this behavior based on requirements
          }

          // Add delay between batches to prevent overwhelming the server
          if (i < totalBatches - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        }

        if (failedBatches > 0) {
          console.warn(
            `⚠️ ${failedBatches} out of ${totalBatches} batches failed to upload`
          );
          // You could show a notification to the user here
        }
      }

      resetFileInput(); // Reset file input and clear files after upload attempt

      // Keep progress visible for a moment before hiding
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Reset batch upload progress
      setBatchUploadProgress({
        isUploading: false,
        currentBatch: 0,
        totalBatches: 0,
        totalFiles: 0
      });

      // Add a small delay to ensure server processing is complete
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Refetch the documents to show the newly uploaded files
      await refetchDocuments();
    } catch (error) {
      console.error("❌ Error uploading documents:", error);

      // Reset batch upload progress on error
      setBatchUploadProgress({
        isUploading: false,
        currentBatch: 0,
        totalBatches: 0,
        totalFiles: 0
      });
    }
  };

  const handleDeleteDocument = async (
    documentId: string | number,
    documentGuid?: string
  ) => {
    try {
      // Check if this is an Engineering document type
      const isEngineeringDocument = documentType === "engineering";

      if (isEngineeringDocument) {
        // For Engineering documents, use the documentGuid (UUID) if available, otherwise fall back to documentId
        const idToDelete = documentGuid || documentId;

        const response = await ProcessingHubService.deleteEngineeringDocument(
          idToDelete
        );

        // Check if the response indicates success
        if (response && response.value && response.value.isSuccess === false) {
          throw new Error(
            `Engineering API returned failure: ${JSON.stringify(
              response.value
            )}`
          );
        }

        return response;
      } else {
        // For other document types, use the regular delete mutation (04) with documentId
        const result = await deleteDocument(documentId).unwrap();
        return result;
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      throw error;
    }
  };

  // Export/Download functionality
  const getDocumentName = (documentDetails: any) => {
    if (documentDetails?.status !== DocumentStatus.FINALIZED) {
      return `${
        documentDetails?.documentName || documentDetails?.fileName || "Document"
      }_DRAFT`;
    } else {
      return `${
        documentDetails?.documentName || documentDetails?.fileName || "Document"
      }_FINALIZED`;
    }
  };

  const handleExportDocument = async (documentId: string | number) => {
    try {
      // Get the transformed documents data
      const transformedDocuments = transformDocumentsData(documentsData);

      // Find the document details from the transformed documents data
      const documentDetails = transformedDocuments.find(
        (doc: any) => doc.id === documentId || doc.documentGuid === documentId
      );

      if (!documentDetails) {
        console.error("Document not found for export");
        return;
      }

      // Call the download function with the document details
      const downloadResult = await downloadFile({
        documentGuid: documentDetails.documentGuid || documentId,
        documentName: "Document-Report__" + getDocumentName(documentDetails),
        downloadType: "document report"
      });
    } catch (error) {
      console.error("Error exporting document:", error);
    }
  };

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
  };

  const mockDocuments = [
    {
      id: 1,
      fileName: "IOGP - 12345",
      flowName: "XYZ123",
      docType: "Doc",
      uploadedDate: "6th Oct 2025",
      activityStatus: "Error",
      details: {
        status: "Not started",
        rewrite: "Not started",
        tagging: "Not started"
      }
    }
  ];

  const documents = documentsData || mockDocuments;

  // Get pagination info from the API response
  const paginationInfo = documentsData
    ? {
        currentPage: documentsData.page || currentPage,
        totalPages: documentsData.totalPages || 1,
        pageSize: documentsData.size || pageSize,
        totalElements: documentsData.totalElements || 0
      }
    : {
        currentPage: 1,
        totalPages: 1,
        pageSize: 10,
        totalElements: 0
      };

  return (
    <>
      {/* <HeaderNew showSearch={false} /> */}
      <div className="doc-setup-header">
        <div className="hub-breadcrumb">
          <span className="breadcrumb-link" onClick={() => navigate("/")}>
            Home
          </span>
          <span className="breadcrumb-separator">&gt;</span>
          <span
            className="breadcrumb-link"
            onClick={() => navigate("/processing-hub")}
          >
            Processing Hub
          </span>
          <span className="breadcrumb-separator">&gt;</span>
          <span className="breadcrumb-current">Start Processing</span>
        </div>
        <h1 className="hub-title">Processing Hub</h1>
        <p className="hub-subtitle">
          Unified entry point for all engineering documents, drawings,
          P&amp;IDs, specifications, and content.
        </p>
      </div>
      <div className="processing-upload-container">
        {/* Step 1: Select the Purpose */}
        <div className="ph-panel purpose-panel">
          <div className="ph-panel-header">
            <span className="ph-step-badge">1</span>
            <h2 className="ph-panel-title">Select the Purpose</h2>
          </div>
          <div className="purpose-cards">
            <button
              type="button"
              className={`purpose-card${
                selectedPurpose === "drawing" ? " selected" : ""
              }`}
              onClick={() => setSelectedPurpose("drawing")}
            >
              <span className="purpose-card-radio">
                {selectedPurpose === "drawing" ? (
                  <CheckCircleIcon className="radio-checked" />
                ) : (
                  <RadioButtonUncheckedIcon className="radio-unchecked" />
                )}
              </span>
              <span className="purpose-card-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="11"
                  height="18"
                  viewBox="0 0 11 18"
                  fill="none"
                >
                  <path
                    d="M0.25 18L0 15.8L2.85 7.95C3.1 8.18333 3.37083 8.37917 3.6625 8.5375C3.95417 8.69583 4.26667 8.81667 4.6 8.9L1.85 16.45L0.25 18ZM10.75 18L9.15 16.45L6.4 8.9C6.73333 8.81667 7.04583 8.69583 7.3375 8.5375C7.62917 8.37917 7.9 8.18333 8.15 7.95L11 15.8L10.75 18ZM5.5 8C4.66667 8 3.95833 7.70833 3.375 7.125C2.79167 6.54167 2.5 5.83333 2.5 5C2.5 4.35 2.6875 3.77083 3.0625 3.2625C3.4375 2.75417 3.91667 2.4 4.5 2.2V0H6.5V2.2C7.08333 2.4 7.5625 2.75417 7.9375 3.2625C8.3125 3.77083 8.5 4.35 8.5 5C8.5 5.83333 8.20833 6.54167 7.625 7.125C7.04167 7.70833 6.33333 8 5.5 8ZM5.5 6C5.78333 6 6.02083 5.90417 6.2125 5.7125C6.40417 5.52083 6.5 5.28333 6.5 5C6.5 4.71667 6.40417 4.47917 6.2125 4.2875C6.02083 4.09583 5.78333 4 5.5 4C5.21667 4 4.97917 4.09583 4.7875 4.2875C4.59583 4.47917 4.5 4.71667 4.5 5C4.5 5.28333 4.59583 5.52083 4.7875 5.7125C4.97917 5.90417 5.21667 6 5.5 6Z"
                    fill="#5F5E5E"
                  />
                </svg>
              </span>
              <span className="purpose-card-title">Engineering Drawing</span>
              <span className="purpose-card-desc">
                Analyze P&amp;IDs, Isometrics and Mechanical Drawings.
              </span>
            </button>
            <button
              type="button"
              className={`purpose-card${
                selectedPurpose === "documents" ? " selected" : ""
              }`}
              onClick={() => setSelectedPurpose("documents")}
            >
              <span className="purpose-card-radio">
                {selectedPurpose === "documents" ? (
                  <CheckCircleIcon className="radio-checked" />
                ) : (
                  <RadioButtonUncheckedIcon className="radio-unchecked" />
                )}
              </span>
              <span className="purpose-card-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.95831 2.29102V17.7077H11.6666C12.0118 17.7077 12.2916 17.9875 12.2916 18.3327C12.2916 18.6779 12.0118 18.9577 11.6666 18.9577H3.83331C3.21199 18.9577 2.70831 18.454 2.70831 17.8327V2.16602C2.70831 1.54469 3.21199 1.04102 3.83331 1.04102H13.5429C13.8412 1.04102 14.1274 1.15954 14.3384 1.37052L16.9621 3.99429C17.1731 4.20527 17.2916 4.49142 17.2916 4.78979V10.8327C17.2916 11.1779 17.0118 11.4577 16.6666 11.4577C16.3215 11.4577 16.0416 11.1779 16.0416 10.8327V4.84157L13.4911 2.29102H3.95831Z"
                    fill="#6A5F00"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M13.3333 1.04102C13.6785 1.04102 13.9583 1.32084 13.9583 1.66602V4.37435H16.6666C17.0118 4.37435 17.2916 4.65417 17.2916 4.99935C17.2916 5.34453 17.0118 5.62435 16.6666 5.62435H13.8333C13.212 5.62435 12.7083 5.12067 12.7083 4.49935V1.66602C12.7083 1.32084 12.9881 1.04102 13.3333 1.04102Z"
                    fill="#6A5F00"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M15.3914 18.7746C15.6354 19.0187 16.0312 19.0187 16.2753 18.7746L18.7753 16.2746C19.0193 16.0305 19.0193 15.6348 18.7753 15.3907L16.2753 12.8907C16.0312 12.6467 15.6354 12.6467 15.3914 12.8907C15.1473 13.1348 15.1473 13.5305 15.3914 13.7746L16.8244 15.2077H13.3333C12.9881 15.2077 12.7083 15.4875 12.7083 15.8327C12.7083 16.1779 12.9881 16.4577 13.3333 16.4577H16.8244L15.3914 17.8907C15.1473 18.1348 15.1473 18.5305 15.3914 18.7746Z"
                    fill="#6A5F00"
                  />
                </svg>
              </span>
              <span className="purpose-card-title">Engineering Documents</span>
              <span className="purpose-card-desc">
                Process data sheets specifications and reports.
              </span>
            </button>
          </div>
        </div>

        {/* Step 2: Upload Content */}
        <div className="ph-panel upload-panel">
          <div className="ph-panel-header">
            <span className="ph-step-badge">2</span>
            <h2 className="ph-panel-title">Upload Content</h2>
          </div>

          <div
            className={`upload-dropzone${files.length > 0 ? " has-files" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.classList.add("drag-over");
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.classList.remove("drag-over");
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              e.currentTarget.classList.remove("drag-over");
              const droppedFiles = Array.from(e.dataTransfer.files).filter(
                (file) => file.size > 0 || file.type !== ""
              );
              if (droppedFiles.length > 0) {
                setFiles((prev) => [...prev, ...droppedFiles]);
              }
            }}
            onClick={handleFileUploadClick}
          >
            <div className="dropzone-icon">
              <CloudUploadIcon className="cloud-upload-icon" />
            </div>
            <p className="dropzone-text">
              Drag and drop files or folders here or{" "}
              <span
                className="browse-link"
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileUploadClick();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFileUploadClick();
                  }
                }}
              >
                browse
              </span>
            </p>
          </div>

          <div className="supported-files-banner">
            <InfoOutlinedIcon className="info-icon" />
            <span>
              Supported file types include .jpg, .jpeg, .png, .csv, .xls, .zip,
              .msg, .doc, .docx, .mp4, .wmv, .mov, .avi, .pdf, .txt, .ppt, .pptx
            </span>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            {...(isFolderMode ? { webkitdirectory: "" } : {})}
            onChange={handleFileChange}
            style={{ display: "none" }}
          />

          {/* Selected files */}
          {files.length > 0 && (
            <div className="upload-file-list">
              {files.map((file, index) => (
                <div key={index} className="upload-file-row">
                  <InsertDriveFileIcon className="file-icon" />
                  <span className="file-name">
                    {file.webkitRelativePath || file.name}
                  </span>
                  <span className="file-type">
                    {selectedPurpose === "drawing"
                      ? "Engineering Drawing"
                      : "Engineering Document"}
                  </span>
                  <span className="file-size">
                    {(file.size / 1024 / 1024).toFixed(1)} mb
                  </span>
                  <button
                    type="button"
                    className="remove-file"
                    aria-label="Remove file"
                    onClick={() => {
                      const newFiles = files.filter((_, i) => i !== index);
                      if (newFiles.length === 0) {
                        resetFileInput();
                      } else {
                        setFiles(newFiles);
                      }
                    }}
                  >
                    <CloseIcon />
                  </button>
                </div>
              ))}
            </div>
          )}

          {files.length > UPLOAD_BATCH_SIZE && (
            <div className="batch-info-notice">
              <p>
                <strong>Notice:</strong> You have selected {files.length} files.
                Files will be uploaded in batches of {UPLOAD_BATCH_SIZE} for
                optimal performance.
              </p>
            </div>
          )}

          <div className="upload-actions">
            <button
              className="upload-attachment-btn"
              onClick={handleUpload}
              disabled={
                files.length === 0 ||
                isUploading ||
                isMultiUploading ||
                batchUploadProgress.isUploading
              }
            >
              <UploadOutlinedIcon style={{ fontSize: 18, marginRight: 6 }} />
              {batchUploadProgress.isUploading
                ? `Uploading batch ${batchUploadProgress.currentBatch}/${batchUploadProgress.totalBatches}...`
                : isUploading || isMultiUploading
                ? "Uploading..."
                : "Upload"}
            </button>
          </div>

          {/* Batch Upload Progress Indicator */}
          {batchUploadProgress.isUploading && (
            <div className="batch-upload-progress">
              <div className="progress-info">
                <p>Upload Progress</p>
                <p>
                  Current batch: {batchUploadProgress.currentBatch} of{" "}
                  {batchUploadProgress.totalBatches}
                </p>
                {batchUploadProgress.totalBatches > 1 && (
                  <p>Please wait while we process your files in batches...</p>
                )}
              </div>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${
                      batchUploadProgress.totalBatches > 0
                        ? (batchUploadProgress.currentBatch /
                            batchUploadProgress.totalBatches) *
                          100
                        : 0
                    }%`
                  }}
                ></div>
              </div>
              <p className="progress-percentage">
                {batchUploadProgress.totalBatches > 0
                  ? Math.round(
                      (batchUploadProgress.currentBatch /
                        batchUploadProgress.totalBatches) *
                        100
                    )
                  : 0}
                % Complete
              </p>
            </div>
          )}
        </div>

        {/* Current Processing Queue */}
        <div className="ph-panel queue-panel">
          <h2 className="queue-title">Current Processing Queue</h2>

          {isLoadingDocuments ? (
            <div
              style={{
                textAlign: "center",
                padding: "40px 30px",
                fontSize: "16px",
                color: "#666"
              }}
            >
              Loading documents...
            </div>
          ) : documentsError ? (
            <div>Error loading documents</div>
          ) : (
            <Table
              data={filterDocumentsByFlowId(
                transformDocumentsData(documentsData)
              )}
              onView={handleViewDocument}
              onDelete={handleDeleteDocument}
              onEdit={handleEditDocument}
              onRefresh={refetchDocuments}
              isLoading={isLoadingDocuments}
              error={documentsError}
              alwaysEnableEdit
            />
          )}

          {/* Pagination Component */}
          {documentsData && paginationInfo.totalPages > 1 && (
            <SimplePagination
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              onPageChange={handlePaginationChange}
            />
          )}
        </div>
      </div>

      {/* Azure Blob Modal */}
      <AzureBlobModal
        isOpen={isAzureBlobModalOpen}
        onClose={() => setIsAzureBlobModalOpen(false)}
        onUpload={handleAzureBlobUpload}
        documentType={documentType}
      />
    </>
  );
};

export default Upload;
